import { IFileResponse } from '@lib/core';
import { DemandProductData, IDemandProductData } from './DemandProductData';
import { DemandProductDelivery, IDemandProductDelivery } from './DemandProductDelivery';
import { DemandProductPrice, IDemandProductPrice } from './DemandProductPrice';
import { DemandDataService } from '../demand-data.service';
import { IDemandProductChangeResponse } from './IDemandProductChangeResponse';
import { ClassificationCode, ItemClassificationDataService } from '@lib/classification';
import { FileDataService } from '@lib/file';
import { firstValueFrom } from 'rxjs';

export class DemandProduct {
    public readonly itemId?: string;
    public quantity = 1;
    public orderUnit = 'C62';
    public materialGroupCode: string | null = null;
    public readonly data: DemandProductData;
    public readonly delivery: DemandProductDelivery;
    public price: DemandProductPrice;
    private _files: IFileResponse[] = [];
    private _classification?: ClassificationCode;
    public comment: IDemandProductNote;
    public purchasingGroup: IPurchasingGroupRef | null = null;
    // EPROC-10907: customer site business key (WERKS)
    public siteCode: string | null = null;

    public static fromDto(
        dto: DemandProductDto,
        demandId: string,
        dataService: DemandDataService,
        itemClassificationDataService: ItemClassificationDataService,
        fileDataService: FileDataService
    ): DemandProduct {
        const product = new DemandProduct(
            dataService,
            itemClassificationDataService,
            fileDataService,
            demandId,
            dto.quantity,
            dto.orderUnit,
            dto.itemId,
            DemandProductData.fromDto(dto.data),
            DemandProductDelivery.fromDto(dto.delivery),
            DemandProductPrice.fromDto(dto.price),
            dto.comment,
            dto.materialGroupCode ?? null
        );
        product.purchasingGroup = dto.purchasingGroup ?? null;
        product.siteCode = dto.siteCode ?? null;
        return product;
    }

    public static copy(
        product: DemandProduct,
        demandId: string,
        dataService: DemandDataService,
        itemClassificationDataService: ItemClassificationDataService,
        fileDataService: FileDataService
    ): DemandProduct {
        const copied = new DemandProduct(
            dataService,
            itemClassificationDataService,
            fileDataService,
            demandId,
            product.quantity,
            product.orderUnit,
            product.itemId,
            DemandProductData.fromDto(product.data),
            DemandProductDelivery.fromDto(product.delivery),
            DemandProductPrice.fromDto(product.price),
            product.comment,
            product.materialGroupCode
        );
        copied.purchasingGroup = product.purchasingGroup;
        copied.siteCode = product.siteCode;
        return copied;
    }

    public get files(): IFileResponse[] {
        return this._files.slice();
    }

    get classification(): ClassificationCode | undefined {
        return this._classification;
    }

    constructor(
        private readonly dataService: DemandDataService,
        private readonly itemClassificationDataService: ItemClassificationDataService,
        private readonly fileDataService: FileDataService,
        private readonly demandId: string,
        quantity = 1,
        orderUnit = 'C62',
        itemId?: string,
        data: DemandProductData = new DemandProductData(),
        delivery: DemandProductDelivery = new DemandProductDelivery(),
        price: DemandProductPrice = new DemandProductPrice(),
        comment: IDemandProductNote = new DemandProductNote(),
        materialGroupCode: string | null = null
    ) {
        if (typeof itemId !== 'undefined') {
            this.itemId = itemId;
        }
        this.data = data;
        this.delivery = delivery;
        this.price = price;
        this.quantity = quantity;
        this.orderUnit = orderUnit;
        this.materialGroupCode = materialGroupCode ?? null;
        if (comment) {
            this.comment = comment;
        } else {
            this.comment = new DemandProductNote();
        }
        if (itemId) {
            this.fileDataService
                .getFiles(demandId, itemId)
                .subscribe((files: IFileResponse[]) => (this._files = files));
        } else {
            this._files = [];
        }
    }

    public toDto(): DemandProductDto {
        return new DemandProductDto(
            this.quantity,
            this.orderUnit,
            this.data,
            this.price,
            this.delivery,
            this.comment,
            this.itemId,
            this.materialGroupCode
        );
    }

    public async updateQuantity(quantity: number): Promise<IDemandProductChangeResponse> {
        this.quantity = quantity;
        return this.save();
    }

    public async save(): Promise<IDemandProductChangeResponse> {
        const response: IDemandProductChangeResponse = await firstValueFrom(
            this.dataService.editProduct(this.demandId, this)
        );
        this.price = response.changedProduct.price; // update price with api data

        return response;
    }

    public async addFile(formData: FormData, internal: boolean): Promise<void> {
        if (this.itemId) {
            this._files = await firstValueFrom(
                this.fileDataService.addFile(formData, this.demandId, internal, this.itemId)
            );
        }
    }

    public async deleteFile(file: IFileResponse): Promise<void> {
        if (this.itemId) {
            this._files = await firstValueFrom(this.fileDataService.deleteFile(file.id, this.demandId, this.itemId));
        }
    }

    public async loadClassification(): Promise<void> {
        if (this.itemId) {
            this._classification = await firstValueFrom(
                this.itemClassificationDataService.getClassification(this.demandId, this.itemId)
            );
        }
    }

    /**
     * Applies a classification already fetched elsewhere (e.g. a batch lookup for many positions at
     * once, see Demand.getProductsAsArticles(), EPROC-11619) instead of issuing this position's own
     * request via {@link loadClassification}.
     */
    public applyClassification(classification: ClassificationCode | undefined): void {
        this._classification = classification;
    }

    public async removeClassification(): Promise<void> {
        if (this.itemId) {
            await firstValueFrom(this.itemClassificationDataService.removeClassification(this.demandId, this.itemId));
        }
    }

    public async changeClassification(code: ClassificationCode): Promise<void> {
        if (this.itemId) {
            await firstValueFrom(
                this.itemClassificationDataService.changeClassification(this.demandId, this.itemId, code)
            );
        }
    }

    public get valid(): boolean {
        return this.orderUnit !== '' && this.data.contentUnit !== '';
    }
}

export interface IDemandProduct {
    itemId?: string;
    quantity: number;
    orderUnit: string;
    materialGroupCode?: string | null;
    data: IDemandProductData;
    delivery: IDemandProductDelivery;
    price: IDemandProductPrice;
    comment: IDemandProductNote;
    purchasingGroup?: IPurchasingGroupRef | null;
    siteCode?: string | null;
}

export interface IPurchasingGroupRef {
    id: string;
    code: string;
    name: string;
}

export interface IDemandProductNote {
    comment: string;
    internalComment: string;
}

export class DemandProductNote implements IDemandProductNote {
    constructor(public comment = '', public internalComment = '') {}
}

export class DemandProductDto implements IDemandProduct {
    itemId?: string;
    quantity: number;
    orderUnit: string;
    materialGroupCode?: string | null;
    data: IDemandProductData;
    delivery: IDemandProductDelivery;
    price: IDemandProductPrice;
    comment: IDemandProductNote;
    purchasingGroup?: IPurchasingGroupRef | null;
    siteCode?: string | null;

    constructor(
        quantity: number,
        orderUnit: string,
        data: IDemandProductData,
        price: IDemandProductPrice,
        delivery: IDemandProductDelivery,
        comment: IDemandProductNote,
        itemId?: string,
        materialGroupCode: string | null = null,
        purchasingGroup: IPurchasingGroupRef | null = null
    ) {
        this.data = data;
        this.delivery = delivery;
        this.itemId = itemId;
        this.orderUnit = orderUnit;
        this.materialGroupCode = materialGroupCode;
        this.price = price;
        this.quantity = quantity;
        this.comment = comment;
        this.purchasingGroup = purchasingGroup;
    }
}
